import React, { useState } from 'react';
import { 
  IconButton, 
  Tooltip, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
  Alert,
  Chip
} from '@mui/material';
import { 
  PowerSettingsNew as PowerIcon,
  Power as PowerOnIcon,
  PowerOff as PowerOffIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import './DeviceControlButton.css';

const DeviceControlButton = ({
    dispositivo,
    tipo,
    onCambiarEstado,
    disabled = false,
    loading = false,
    showFullPanel = false
}) => {
    const [isChanging, setIsChanging] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    const handleConfirm = async () => {
        if (onCambiarEstado && !disabled && !loading) {
            setIsChanging(true);
            setDialogOpen(false);
            
            try {
                await onCambiarEstado(dispositivo.id || dispositivo._id, !dispositivo.estatusDispositivo);
                
                // Mostrar SweetAlert de éxito
                Swal.fire({
                    icon: 'success',
                    title: '¡Éxito!',
                    text: `${dispositivo.nombre || dispositivo.alias} ${!dispositivo.estatusDispositivo ? 'encendido' : 'apagado'} correctamente`,
                    timer: 3000,
                    showConfirmButton: false,
                    toast: true,
                    position: 'top-end',
                    timerProgressBar: true,
                    showCloseButton: true,
                    customClass: {
                        popup: 'swal-high-z-index',
                        container: 'swal-high-z-index'
                    }
                });
                
                // Pequeño delay para que se vea la transición
                setTimeout(() => {
                    setIsChanging(false);
                }, 500);
            } catch (error) {
                setIsChanging(false);
                
                // Extraer el mensaje de error correcto
                let errorMessage = '';
                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                } else {
                    errorMessage = 'Error al cambiar estado del dispositivo';
                }
                
                // Mostrar SweetAlert específico para diferentes tipos de error
                if (errorMessage.includes('tarjeta está apagada')) {
                    Swal.fire({
                        icon: 'warning',
                        title: 'Tarjeta Apagada',
                        text: 'No se puede encender el sensor porque su tarjeta está apagada',
                        confirmButtonText: 'Entendido',
                        confirmButtonColor: '#ff9800',
                        showCloseButton: true,
                        customClass: {
                            popup: 'swal-high-z-index',
                            container: 'swal-high-z-index'
                        }
                    });
                } else if (errorMessage.includes('alwaysOn')) {
                    Swal.fire({
                        icon: 'info',
                        title: 'Dispositivo Crítico',
                        text: 'Este dispositivo no puede apagarse (configuración crítica)',
                        confirmButtonText: 'Entendido',
                        confirmButtonColor: '#2196f3',
                        showCloseButton: true,
                        customClass: {
                            popup: 'swal-high-z-index',
                            container: 'swal-high-z-index'
                        }
                    });
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: errorMessage,
                        confirmButtonText: 'Intentar de nuevo',
                        confirmButtonColor: '#f44336',
                        showCloseButton: true,
                        customClass: {
                            popup: 'swal-high-z-index',
                            container: 'swal-high-z-index'
                        }
                    });
                }
            }
        }
    };

    const handleOpenDialog = () => {
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    const isOn = dispositivo.estatusDispositivo;
    const isLoading = loading || isChanging;
    const isDisabled = disabled || isLoading;
    const canBeControlled = !dispositivo.alwaysOn;

    const actionText = isOn ? 'apagar' : 'encender';
    const tipoText = tipo === 'sensor' ? 'sensor' : 'tarjeta';

    const tooltipTitle = dispositivo.alwaysOn 
        ? `Esta ${tipoText} no puede apagarse (configuración crítica)`
        : `${isOn ? 'Apagar' : 'Encender'} ${tipoText} "${dispositivo.nombre || dispositivo.alias}"`;

    // Si queremos mostrar el panel completo
    if (showFullPanel) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Estado actual */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                        Estado actual:
                    </Typography>
                    <Chip 
                        label={isOn ? 'Encendido' : 'Apagado'}
                        color={isOn ? 'success' : 'error'}
                        size="small"
                        icon={isOn ? <PowerOnIcon /> : <PowerOffIcon />}
                    />
                </Box>

                {/* Alerta para dispositivos críticos */}
                {dispositivo.alwaysOn && (
                    <Alert severity="info" size="small">
                        <Typography variant="caption">
                            Este dispositivo está marcado como crítico y no puede ser apagado.
                        </Typography>
                    </Alert>
                )}

                {/* Botón de control */}
                {canBeControlled && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            onClick={handleOpenDialog}
                            disabled={isDisabled}
                            variant="contained"
                            startIcon={
                                isLoading ? <CircularProgress size={20} /> : 
                                isOn ? <PowerOffIcon /> : <PowerOnIcon />
                            }
                            sx={{
                                backgroundColor: isOn ? 'error.main' : 'success.main',
                                color: 'white',
                                minWidth: 140,
                                '&:hover': {
                                    backgroundColor: isOn ? 'error.dark' : 'success.dark'
                                },
                                '&:disabled': {
                                    backgroundColor: isOn ? 'error.light' : 'success.light'
                                }
                            }}
                        >
                            {isLoading ? 'Cambiando...' : isOn ? 'Apagar' : 'Encender'}
                        </Button>
                    </Box>
                )}

                {/* Diálogo de confirmación */}
                <Dialog open={dialogOpen} onClose={handleCloseDialog}>
                    <DialogTitle>
                        Confirmar {actionText} dispositivo
                    </DialogTitle>
                    <DialogContent>
                        <Typography gutterBottom>
                            ¿Está seguro de que desea {actionText} {isOn ? 'este' : 'esta'} {tipoText}?
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                            {dispositivo.nombre || dispositivo.alias}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Estado actual: {isOn ? 'Encendido' : 'Apagado'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Estado nuevo: {isOn ? 'Apagado' : 'Encendido'}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} color="inherit">
                            Cancelar
                        </Button>
                        <Button 
                            onClick={handleConfirm} 
                            variant="contained" 
                            color={isOn ? 'error' : 'success'}
                            sx={{
                                backgroundColor: isOn ? 'error.main' : 'success.main',
                                '&:hover': {
                                    backgroundColor: isOn ? 'error.dark' : 'success.dark'
                                }
                            }}
                        >
                            {isOn ? 'Apagar' : 'Encender'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    // Versión original del botón (para compatibilidad)
    // No mostrar botón si es alwaysOn y está encendido
    if (dispositivo.alwaysOn && dispositivo.estatusDispositivo) {
        return null;
    }

    const button = (
        <IconButton
            onClick={handleOpenDialog}
            disabled={isDisabled}
            sx={{
                color: isOn ? 'success.main' : 'error.main',
                transition: 'color 0.3s ease-in-out',
                '&:disabled': {
                    color: isOn ? 'success.light' : 'error.light'
                }
            }}
        >
            {isLoading ? <CircularProgress size={20} /> : <PowerIcon />}
        </IconButton>
    );

    return (
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Tooltip title={tooltipTitle} placement="top">
                {button}
            </Tooltip>
            
            
            <Dialog open={dialogOpen} onClose={handleCloseDialog}>
                <DialogTitle>
                    Confirmar {actionText} dispositivo
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        ¿Está seguro de que desea {actionText} {isOn ? 'este' : 'esta'} {tipoText}?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                        {dispositivo.nombre || dispositivo.alias}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Estado actual: {isOn ? 'Encendido' : 'Apagado'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Estado nuevo: {isOn ? 'Apagado' : 'Encendido'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="inherit">
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleConfirm} 
                        variant="contained" 
                        color={isOn ? 'error' : 'success'}
                        sx={{
                            backgroundColor: isOn ? 'error.main' : 'success.main',
                            '&:hover': {
                                backgroundColor: isOn ? 'error.dark' : 'success.dark'
                            }
                        }}
                    >
                        {isOn ? 'Apagar' : 'Encender'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

DeviceControlButton.propTypes = {
    dispositivo: PropTypes.shape({
        id: PropTypes.string,
        _id: PropTypes.string,
        nombre: PropTypes.string,
        alias: PropTypes.string,
        estatusDispositivo: PropTypes.bool.isRequired,
        alwaysOn: PropTypes.bool
    }).isRequired,
    tipo: PropTypes.oneOf(['sensor', 'tarjeta']).isRequired,
    onCambiarEstado: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    showFullPanel: PropTypes.bool
};

export default DeviceControlButton;
